import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Doughnut } from 'react-chartjs-2';
import { Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faExpand } from '@fortawesome/free-solid-svg-icons';
import '../../../theme/Layout/ChartConfig';
import {
  ChartCard,
  ChartCardHeader,
  ChartCardTitle,
  ChartCardSubtitle,
  ChartCardActions,
  ExpandButton,
  NewOrdersBanner,
  NewOrdersLeft,
  NewOrdersBadge,
  NewOrdersLabel,
  LiveDot,
  NewOrdersLink,
  MiniStatsRow,
  MiniStatTile,
  MiniStatValue,
  MiniStatLabel,
  DonutRow,
  DonutChartWrapper,
  DonutCenterValue,
  BreakdownList,
  BreakdownRow,
  BreakdownHeader,
  BreakdownLabel,
  BreakdownValue,
  BreakdownBarTrack,
  BreakdownBarFill,
  StyledSelect,
  ModalSubtitle,
} from './Styles';
import { PERIOD_FILTER_OPTIONS } from './Constant';
import PlatformFilterDropdown from './PlatformFilterDropdown';
import type { OrdersSummaryCardProps } from './types';

const OrdersSummaryCard: React.FC<OrdersSummaryCardProps> = ({ newOrders, breakdown, isDarkMode = false }) => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('weekly');
  const [channel, setChannel] = useState('all');
  const [expanded, setExpanded] = useState(false);
  const total = breakdown.reduce((sum, item) => sum + item.value, 0) || 1;

  const data = {
    labels: breakdown.map((item) => item.label),
    datasets: [
      {
        data: breakdown.map((item) => item.value),
        backgroundColor: breakdown.map((item) => item.color),
        hoverBackgroundColor: breakdown.map((item) => item.color),
        borderWidth: 0,
      },
    ],
  };

  const options = {
    cutout: '72%',
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: isDarkMode ? '#1a202c' : '#fff',
        titleColor: isDarkMode ? '#fff' : '#1a202c',
        bodyColor: isDarkMode ? '#fff' : '#1a202c',
        borderColor: isDarkMode ? '#4a5568' : '#e2e8f0',
        borderWidth: 1,
      },
    },
  };

  const renderBreakdownList = () => (
    <BreakdownList>
      {breakdown.map((item) => {
        const percent = (item.value / total) * 100;
        return (
          <BreakdownRow key={item.label}>
            <BreakdownHeader>
              <BreakdownLabel color={item.color}>
                {item.label} ({percent.toFixed(0)}%)
              </BreakdownLabel>
              <BreakdownValue>{item.value}</BreakdownValue>
            </BreakdownHeader>
            <BreakdownBarTrack>
              <BreakdownBarFill percent={percent} color={item.color} />
            </BreakdownBarTrack>
          </BreakdownRow>
        );
      })}
    </BreakdownList>
  );

  return (
    <ChartCard>
      <ChartCardHeader>
        <div>
          <ChartCardTitle>Orders Summary</ChartCardTitle>
          <ChartCardSubtitle>Status breakdown for today's orders</ChartCardSubtitle>
        </div>
        <ChartCardActions>
          <ExpandButton aria-label="Expand chart" onClick={() => setExpanded(true)}>
            <FontAwesomeIcon icon={faExpand} />
          </ExpandButton>
          <PlatformFilterDropdown value={channel} onChange={setChannel} />
          <StyledSelect
            value={period}
            onChange={setPeriod}
            options={PERIOD_FILTER_OPTIONS}
            dropdownStyle={{ borderRadius: 12, overflow: 'hidden' }}
          />
        </ChartCardActions>
      </ChartCardHeader>

      <NewOrdersBanner>
        <NewOrdersLeft>
          <NewOrdersBadge>{newOrders}</NewOrdersBadge>
          <NewOrdersLabel>
            New Orders <LiveDot />
          </NewOrdersLabel>
        </NewOrdersLeft>
        <NewOrdersLink onClick={() => navigate('/orders')}>
          Manage orders <FontAwesomeIcon icon={faArrowRight} />
        </NewOrdersLink>
      </NewOrdersBanner>

      <MiniStatsRow>
        {breakdown.map((item) => (
          <MiniStatTile key={item.label}>
            <MiniStatValue>{item.value}</MiniStatValue>
            <MiniStatLabel>{item.label}</MiniStatLabel>
          </MiniStatTile>
        ))}
      </MiniStatsRow>

      <DonutRow>
        <DonutChartWrapper>
          <Doughnut data={data} options={options} />
          <DonutCenterValue>{total}</DonutCenterValue>
        </DonutChartWrapper>
        {renderBreakdownList()}
      </DonutRow>

      <Modal
        open={expanded}
        onCancel={() => setExpanded(false)}
        footer={null}
        title="Orders Summary"
        width={720}
      >
        <ModalSubtitle>Status breakdown for today's orders</ModalSubtitle>
        <DonutRow>
          <DonutChartWrapper style={{ width: 280, height: 280 }}>
            <Doughnut data={data} options={options} />
            <DonutCenterValue style={{ fontSize: 32 }}>{total}</DonutCenterValue>
          </DonutChartWrapper>
          {renderBreakdownList()}
        </DonutRow>
      </Modal>
    </ChartCard>
  );
};

export default OrdersSummaryCard;
